import { generateTrigrams, LocaleSettings } from '@cf-shared/shared-util';
import { Equipment, isEquipmentIdTemplate, isEquipmentValid } from '@cf-shared/data-model/equipment';
import { CustomExercise, isCustomExerciseById } from '@cf-shared/data-model/exercise';
import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { FitnessState } from '@cf-shared/data-model/user-state';
import { getEquipmentTemplates } from 'pages/equipment/equipment-templates';
import type { DocWatcher } from 'services/database-wrapper';
import type { ExerciseDbService } from 'services/exercise-db.service';
import type { UserDb } from 'services/user-db.service';
import { duplicateEquipment } from './equipment-helpers';
import { statRecordsEquipment } from './exercise-stat-equipment';

// The list items the equipment pickers show, and the rules that choose a machine for an
// exercise from them. Plain functions with every service handed in, so the workout and
// program-settings screens can use them without importing the equipment search page, and
// a unit test can load them without Angular (df-dc0cb76c9a31).

export interface EquipmentUIItem {
  id: string;
  equipment: Equipment;
  isTemplate: boolean;
  trigrams: Set<string>;  // used for search
  usedBy: string[]; // actual names of exercises that use this equipment
}

export async function createEquipmentUIItemAsync(fitnessState: FitnessState, exerciseDb: ExerciseDbService, allCustomExercises: CustomExercise[], equipment: Equipment, watcher: DocWatcher): Promise<EquipmentUIItem> {

  let listOfExercises: string[] = [];
  for (let eid of Object.keys(fitnessState.exerciseStats)) {
    let e = fitnessState.exerciseStats[eid];
    // Check both unit-specific and legacy equipmentId
    const hasEquipment = statRecordsEquipment(e, equipment.id);

    if (hasEquipment) {
      if (isCustomExerciseById(eid)) {
        let customExercise = allCustomExercises.find(e => e._id === eid);
        if (customExercise) {
          listOfExercises.push(customExercise.name);
        }
      } else {
        listOfExercises.push(await exerciseDb.getExerciseNameByIdAsync(eid, watcher));
      }
    }
  }

  return {
    id: equipment.id,
    equipment: equipment,
    isTemplate: isEquipmentIdTemplate(equipment.id),
    trigrams: generateTrigrams(equipment.name),
    usedBy: listOfExercises,
  };
}

// Find the best equipment to use based on a particular equipment template.
// Here, best is one that was most recently used in any exercise so far.
// It the user hasn't yet created an equipment based on that template, this will create one,
// unless createFromTemplateIfMissing says not to. A caller passes false when it has a better
// source for a machine than a generic built-in template — the workout screen's weight-unit
// switch (#5345) prefers copying the machine the person already configured in the other unit,
// and only creates one from the template when that is not there either.
export function getBestEquipmentIdBasedOnTemplate(sourceEquipmentsUI: EquipmentUIItem[], fitnessState: FitnessState, fitnessPlan: FitnessPlan, equipmentTemplateId: string, desiredUnit?: 'kg' | 'lb', createFromTemplateIfMissing = true) {
  // get all subslots that are in use currently, or have been used before
  let currentSubSlots = Object.values(fitnessPlan.subSlots) || [];
  let oldSubslots = fitnessState.oldSubslots || [];
  let allSubslots = currentSubSlots.concat(oldSubslots);

  // find actual equipment configs (already created) that are based on that template
  // If desiredUnit is specified, also filter by unit
  let basedOnExerciseTemplateEquipment = sourceEquipmentsUI.filter(e => {
    const isBasedOnTemplate = !isEquipmentIdTemplate(e.id) && e.equipment.basedOnTemplateId === equipmentTemplateId;
    if (!desiredUnit) {
      return isBasedOnTemplate;
    }
    // Check if equipment unit matches desired unit
    return isBasedOnTemplate && e.equipment.weight_unit === desiredUnit;
  });

  // For each equipment, find the last time it was used by some exercise in the plan
  let basedOnExerciseTemplateEquipmentLastTimeUsed = basedOnExerciseTemplateEquipment.map(e => {
    let lastTimeUsed_utc: string = undefined;
    for (let subSlot of allSubslots) {
      let exerciseStat = fitnessState.exerciseStats[subSlot.exercise_id];
      if (!exerciseStat) {
        continue;
      }

      // Check both unit-specific and legacy equipmentId
      const hasEquipment = statRecordsEquipment(exerciseStat, e.id);

      if (hasEquipment && (lastTimeUsed_utc === undefined || subSlot.lastTimeConcluded_utc > lastTimeUsed_utc)) {
        lastTimeUsed_utc = subSlot.lastTimeConcluded_utc;
      }
    }
    return { equipment: e, lastTimeUsed_utc };
  });

  // find the most recently used one
  basedOnExerciseTemplateEquipmentLastTimeUsed = basedOnExerciseTemplateEquipmentLastTimeUsed.filter(e => e.lastTimeUsed_utc !== undefined);
  basedOnExerciseTemplateEquipmentLastTimeUsed = basedOnExerciseTemplateEquipmentLastTimeUsed.sort((a, b) => { return a.lastTimeUsed_utc > b.lastTimeUsed_utc ? -1 : 1 });
  if (basedOnExerciseTemplateEquipmentLastTimeUsed.length > 0) {
    return basedOnExerciseTemplateEquipmentLastTimeUsed[0].equipment.id;
  }

  // in case the above fails to find anything (which shouldn't, but for safety), just return the first one
  if (basedOnExerciseTemplateEquipment.length > 0) {
    return basedOnExerciseTemplateEquipment[0].equipment.id;
  }

  // no user-generated equipment based on that template yet. the caller may not want a fresh
  // copy of the built-in template made for them (#5345)
  if (!createFromTemplateIfMissing) {
    return undefined;
  }

  // find the template itself
  const templateEquipmentUI = sourceEquipmentsUI.find(e => e.id === equipmentTemplateId);
  // if the template isn't in the source, bail out — the previous alphabetical-first
  // fallback (sourceEquipmentsUI[0]) silently picked a wrong-type match (#3162)
  if (!templateEquipmentUI || !isEquipmentValid(templateEquipmentUI.equipment)) {
    return undefined;
  }

  // Check if we already have equipment for the opposite unit to determine if this is alternate unit creation
  let isAlternateUnit = false;
  if (desiredUnit && isEquipmentIdTemplate(templateEquipmentUI.equipment.id)) {
    // Check if we have equipment for the opposite unit based on this template
    const oppositeUnit = desiredUnit === 'kg' ? 'lb' : 'kg';
    const hasOppositeUnitEquipment = sourceEquipmentsUI.some(e =>
      !isEquipmentIdTemplate(e.id) &&
      e.equipment.basedOnTemplateId === equipmentTemplateId &&
      e.equipment.weight_unit === oppositeUnit
    );
    isAlternateUnit = hasOppositeUnitEquipment;
  }

  // make a user copy of it (unless this is already itself a user copy)
  let newEquipment = !isEquipmentIdTemplate(templateEquipmentUI.equipment.id) ? templateEquipmentUI.equipment : duplicateEquipment(fitnessState, templateEquipmentUI.equipment, isAlternateUnit);

  return newEquipment.id;
}

export async function getSourceEquipments(fitnessState: FitnessState, exerciseDb: ExerciseDbService, userDb: UserDb, locale: LocaleSettings, excludeTemplates: boolean, overrideUnit?: 'kg' | 'lb', watcher?: DocWatcher) {

  let allCustomExercises = await userDb.getAllCustomExercisesAsync();
  // list of all potential exercises to copy equipment from
  let sourceEquipment: EquipmentUIItem[] = await Promise.all(Object.keys(fitnessState.allEquipment || {})
    .map(async id => (createEquipmentUIItemAsync(fitnessState, exerciseDb, allCustomExercises, fitnessState.allEquipment[id], watcher))));

  sourceEquipment = sourceEquipment.sort((a, b) => { return ('' + a.equipment.name).localeCompare(b.equipment.name); });

  if (!excludeTemplates) {
    // If we have an override unit, create a temporary locale that will return that unit
    let effectiveLocale = locale;
    if (overrideUnit) {
      // Create a locale that will make getEquipmentTemplates use the desired unit
      // We use localeName to determine the unit - US locale uses lb, others use kg
      effectiveLocale = { ...locale, localeName: overrideUnit === 'lb' ? 'US' : 'NO' };
    }
    let templates: Equipment[] = getEquipmentTemplates(effectiveLocale);
    let templateItems: EquipmentUIItem[] = await Promise.all(templates.map(async t => createEquipmentUIItemAsync(fitnessState, exerciseDb, allCustomExercises, t, watcher)));
    sourceEquipment.push(...templateItems);

    // for each that is not a template, and doesn't have a basedOnTemplateId, determine it based on first template with the matching type
    // (this is a fixup for older versions before we added the basedOnTemplateId member)
    for (let c of sourceEquipment) {
      if (!isEquipmentIdTemplate(c.id) && !c.equipment.basedOnTemplateId) {
        let template = templates.find(t => t.type === c.equipment.type);
        if (template) {
          c.equipment.basedOnTemplateId = template.id;
        }
      }
    }
  }

  return sourceEquipment;
}
